// Analysis profile: the short text, one per job and one per customer, that
// says how the data is built and that every agent chat on the job reads each
// turn. It shows the card on Admin → Context, the Edit button that types it
// section by section, and the button that opens the agent. It never clicks
// Edit, Accept, Decline, Withdraw or Restore, and never opens the agent:
// nothing is changed.
// Opened on the page itself: /admin/<customer>/context?walkthrough=analysis-profile.

import type { Walkthrough } from './types'

export const analysisProfile: Walkthrough = {
  slug: 'analysis-profile',
  title: 'Analysis profile',
  summary:
    'Shows where a job’s analysis profile lives, what it says, the two ways to write it (typing it section by section, or with the agent), and how a Manager decides what other people propose. Nothing is changed.',
  added: '2026-10-29',
  audience: 'realm',
  steps: [
    {
      page: 'admin-context',
      feature: 'configuration/analysis-profile',
      control: 'Analysis profile',
      say: 'This is the analysis profile, on Context under Configuration. It is a short text that says how a job’s data is built: what a row is, which base filters and variables are live, the weight and the default period. Every agent chat on the job reads it each turn.',
    },
    {
      page: 'admin-context',
      feature: 'configuration/analysis-profile',
      control: 'Context for',
      say: 'Pick a job in Context for to see that job’s profile. With Your organisation picked, the card shows the part that applies to every job of the customer.',
    },
    {
      page: 'admin-context',
      feature: 'configuration/analysis-profile',
      control: 'Edit',
      say: 'Edit opens one box for each section of the profile, such as Base filters, Weight and Time, with a line above each box on what goes in it. Leave a box empty when it does not apply. A Manager publishes; anyone else proposes the change for a Manager to accept.',
    },
    {
      page: 'admin-context',
      feature: 'configuration/analysis-profile',
      control: 'Work on it with the agent',
      say: 'Or let the agent write it. This button opens the agent with a message ready: it asks you about the data, checks each answer against it, and saves only when you say yes. Nothing is sent until you press Send.',
    },
    {
      page: 'admin-context',
      feature: 'configuration/analysis-profile',
      control: 'Version history',
      say: 'Every version is kept. Version history lists them: View shows the text, and a Manager can restore an earlier one, which publishes it again as a new version.',
    },
    {
      page: 'admin-context',
      feature: 'configuration/analysis-profile',
      control: 'Proposed changes',
      say: 'When the person who typed it or asked the agent is not a Manager, the save is a proposal instead. A Manager reads it here and chooses Accept or Decline. Everyone else sees those buttons greyed out with the reason. The author can withdraw their own proposal.',
    },
  ],
}
